import { useMutation } from '@apollo/client';
import { Button, Loading, Stack, Icon, Typography, colors } from '@ui-components/neo.tax';
import { ReactComponent as ChevronDownIcon } from '@ui-components/neo.tax/src/assets/icons/chevron-down.svg';
import * as Ariakit from 'ariakit';
import React, { ReactElement } from 'react';

import { useAscContext } from '@neo.tax/asc/context';
import { useAscProjects, type AscProject } from '@neo.tax/asc/hooks';
import {
  MOVE_TICKETS_TO_ASC_PROJECT,
  MoveTicketsToAscProjectMutation,
  MoveTicketsToAscProjectMutationVariables,
  TicketGroupingKind,
  evictCache,
  useKeywordSearch,
  Menu,
  MenuItem,
} from '@neo.tax/shared';

import { StyledKeywordSearch, MenuWrapper, ProjectList, SearchWrapper } from './asc-tickets-pane.styled';

type MoveAscTicketsButtonProps = {
  /** The IDs of the Tickets to move. */
  ticketIds: Set<string>;
  /**
   * The ID of the Project to move from. This Project's Tickets will be
   * refreshed after the move
   */
  fromAscProjectId: string;
  /** The actual count of tickets that will be moved (used for display) */
  ticketCount: number;
  /**
   * Grouping buckets (epics / collections / no-parent) to expand into tickets for bulk moves.
   * Server resolves each entry against asc_ticket_groups scoped to fromAscProjectId.
   */
  groupings: Array<{ kind: TicketGroupingKind; id: string }>;
  /** Callback to be called when the tickets are moved */
  onCompleted: () => void;
};

const MOVE_TICKETS_REQUEST_KEY = 'MOVE_TICKETS_REQUEST_KEY';

export const MoveAscTicketsButton = ({
  fromAscProjectId,
  groupings,
  onCompleted,
  ticketCount,
  ticketIds: selectedTicketIds,
}: MoveAscTicketsButtonProps): ReactElement => {
  const { searchKeyword, setSearchKeyword } = useKeywordSearch();
  const menuState = Ariakit.useMenuState({ gutter: 1 });
  const { ascProjects, loading: loadingAscProjects } = useAscProjects({ searchKeyword });
  const { fiscalYearId, periodName } = useAscContext();

  const [moveTicketsToAscProject, { loading: loadingMoveTicketsToAscProject }] = useMutation<
    MoveTicketsToAscProjectMutation,
    MoveTicketsToAscProjectMutationVariables
  >(MOVE_TICKETS_TO_ASC_PROJECT);

  const createOnClickHandler =
    (toAscProject: AscProject): (() => Promise<void>) =>
    async (): Promise<void> => {
      await moveTicketsToAscProject({
        variables: {
          ascProjectId: toAscProject.id,
          input: {
            ticketingProviderTicketIds: [...selectedTicketIds],
            groupings,
            sourceAscProjectId: fromAscProjectId,
            fiscalYearId,
            periodName,
          },
          fiscalYearId,
          periodName,
        },
        onCompleted,
        update: (cache) => {
          evictCache(cache, [
            'getAscProjectTeamMembers',
            'getAscProjectTpTickets',
            'getAscProjectGroupingTicketsOrCollections',
          ]);
          cache.modify({
            id: `AscProject:${toAscProject.id}`,
            fields: {
              [`ticketCount({"periodName":${periodName},"fiscalYearId":${fiscalYearId}})`]: (
                cachedTicketCount: number,
              ) => cachedTicketCount + ticketCount,
            },
            broadcast: true,
          });
          cache.modify({
            id: `AscProject:${fromAscProjectId}`,
            fields: {
              [`ticketCount({"periodName":${periodName},"fiscalYearId":${fiscalYearId}})`]: (
                cachedTicketCount: number,
              ) => cachedTicketCount - ticketCount,
            },
            broadcast: true,
          });
        },
        context: {
          serializationKey: MOVE_TICKETS_REQUEST_KEY,
          debounceKey: MOVE_TICKETS_REQUEST_KEY,
          errorToast: {
            title: 'Failed to move tickets to the selected project',
            description:
              'An unexpected error happened while updating. Please try again and if this error persists, contact support.',
          },
          successToast: {
            title: `Tickets moved to ${toAscProject.name}`,
          },
        },
      });
    };

  return (
    <>
      <Button
        as={Ariakit.MenuButton}
        state={menuState}
        size="sm"
        variant="primary"
        rounded="regular"
        disabled={ticketCount === 0 || !ascProjects || loadingMoveTicketsToAscProject}
        style={{ margin: 0 }}
      >
        <Stack gap={2}>
          {loadingMoveTicketsToAscProject ? (
            'Moving...'
          ) : (
            <>
              Move <Icon size={4} node={<ChevronDownIcon />} />
            </>
          )}
        </Stack>
      </Button>
      <Menu state={menuState}>
        <MenuWrapper>
          <SearchWrapper>
            <StyledKeywordSearch searchKeyword={searchKeyword} setSearchKeyword={setSearchKeyword} />
          </SearchWrapper>
          <ProjectList direction="column">
            {loadingAscProjects && <Loading height={160} />}
            {!ascProjects || ascProjects.length === 0 ? (
              <MenuItem>
                <Stack alignItems="center" justifyContent="center" fullHeight>
                  <Typography color={colors.gray[300]} fontSize="lg" fontWeight="semibold" textAlign="center">
                    No projects
                  </Typography>
                </Stack>
              </MenuItem>
            ) : (
              ascProjects.map(
                (toAscProject) =>
                  toAscProject.id !== fromAscProjectId && (
                    <MenuItem key={toAscProject.id} onClick={createOnClickHandler(toAscProject)}>
                      {toAscProject.name}
                    </MenuItem>
                  ),
              )
            )}
          </ProjectList>
        </MenuWrapper>
      </Menu>
    </>
  );
};
